import React, { useEffect, useState } from "react";
import { Route, Switch } from 'react-router-dom';
import '../../styles/internal/internal-styles.css';
import {Box, CircularProgress, Grid} from '@mui/material';
import Colors from "../../constants/Colors";
import ErrorPanel from "../error-pages/ErrorPanel";
import GenericNotFound from "../error-pages/GenericNotFound";
import WebsiteViewComponent from "./account/WebsiteViewComponent";
import ViewJobsComponent from "./account/ViewJobsComponent";
import InternalSiteHeader from "./InternalSiteHeader";
import {getSiteAnalytics, getSiteAnalyticsV2, getSiteByIdForUser} from "../../actions/internal/SiteActions";
import ViewJobDetailsComponent from "./account/ViewJobDetailsComponent";
import ViewSiteBillingComponent from "./account/ViewSiteBillingComponent";
import ViewSiteHoursComponent from "./account/ViewSiteHoursComponent";
import SiteAccountDetails from "./account/views/SiteAccountDetails";
import ViewSiteActivityComponent from "./account/ViewSiteActivityComponent";
import ViewSitePackageComponent from "./account/ViewSitePackageComponent";
import SiteCarePage from "./account/SiteCarePage";
import ShopBlogPage from "./account/ShopBlogPage";
import { getUserId, getUserObject } from "../../selectors/UserSelectors";
import { DesktopBox, MobileBox } from "../common/Boxes";
import { StyledLoadingSection } from "../common/StyledProgressCircle";
import { calculateStartDate, formatDateToYYYYMMDD } from "../../utilities/DateUtilities";
import { getAllSites } from "../../selectors/SiteSelectors";
import { PRIVACY_POLICY } from "../../assets/legal/PrivacyPolicy";
import { validatePolicyAcceptance } from "../../actions/internal/LegalActions";
import SupportCompletedPage from "./support/SupportCompletedPage";
import ViewSiteContentPage from "./account/ViewSiteContentPage";
import AddSiteContentPage from "./account/AddSiteContentPage";
import ViewSiteContentDetailsPage from "./account/ViewSiteContentDetailsPage";
import EditSiteContentDetailsPage from "./account/EditSiteContentDetailsPage";

const InternalSiteRouter = ({ history, match, location }) => {

    const [ isLoading, setIsLoading ] = useState(true);
    const [ error, setError ] = useState(false);

    useEffect(() => {
      const siteId = match.params.site_id;
      
      if (!siteId) return;
      prepareSiteSession(siteId).then();
    }, [match.params.site_id])

    const prepareSiteSession = async (siteId) => {
      try {
        setIsLoading(true);
        const userId = getUserId();
        await getSiteByIdForUser(siteId, userId);
      } catch (ex) {
        setError('Could not load the business data at this time!');
        const err = await ex;
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }

    const loadingContent = (
      <StyledLoadingSection />
    )

    const mainContent = !isLoading && !error ?
        <Switch>
          {/* DASHBOARD ROUTES */}
          <Route exact path="/dashboard/site/:site_id" render={routeProps => <WebsiteViewComponent {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/account" render={routeProps => <SiteAccountDetails {...routeProps} />} />

          {/* BILLING ROUTES */}
          <Route exact path="/dashboard/site/:site_id/account/billing" render={routeProps => <ViewSiteBillingComponent {...routeProps} />} />

          {/* PROJECT ROUTES */}
          <Route exact path="/dashboard/site/:site_id/projects" render={routeProps => <ViewJobsComponent {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/projects/:job_id" render={routeProps => <ViewJobDetailsComponent {...routeProps} />} />

          {/* CONSULTING ROUTES */}
          <Route exact path="/dashboard/site/:site_id/support" render={routeProps => <ViewSiteHoursComponent {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/support/completed" render={routeProps => <SupportCompletedPage {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/hours" render={routeProps => <ViewSiteHoursComponent {...routeProps} />} /> {/* deprecated route */}
          <Route exact path="/dashboard/site/:site_id/consulting" render={routeProps => <ViewSiteHoursComponent {...routeProps} />} /> {/* deprecated route */}

          {/* CONTENT ROUTES */}
          <Route exact path="/dashboard/site/:site_id/content" render={routeProps => <ViewSiteContentPage {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/content/create" render={routeProps => <AddSiteContentPage {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/content/:article_id" render={routeProps => <ViewSiteContentDetailsPage {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/content/:article_id/edit" render={routeProps => <EditSiteContentDetailsPage {...routeProps} />} />

          {/* ACTIVITY ROUTES */}
          <Route exact path="/dashboard/site/:site_id/activity" render={routeProps => <ViewSiteActivityComponent {...routeProps} />} />

          {/* PACKAGE ROUTES */}
          <Route exact path="/dashboard/site/:site_id/hosting" render={routeProps => <ViewSitePackageComponent {...routeProps} />} />
          <Route exact path="/dashboard/site/:site_id/package" render={routeProps => <ViewSitePackageComponent {...routeProps} />} /> {/* deprecated route */}
          <Route exact path="/dashboard/site/:site_id/package/site-care" render={routeProps => <SiteCarePage {...routeProps} />} /> {/* deprecated route */}
          <Route exact path="/dashboard/site/:site_id/package/shop-blog-editor" render={routeProps => <ShopBlogPage {...routeProps} />} /> {/* deprecated route */}

          {/* MISC ROUTES */}
          <Route path="*" render={routeProps => <GenericNotFound {...routeProps} />} />
        </Switch>
        :
        <ErrorPanel
            message={"Oh no! Something went wrong. Please check your internet connection and try again."}
        />;
    return (
        <Box sx={{ flexGrow: 1, display: 'flex' }}>
          {!isLoading && <Box>
            <InternalSiteHeader />
          </Box> }
          <Box sx={{ flexGrow: 1, overflow: 'auto'}}>
            {isLoading ? loadingContent : mainContent}
          </Box>
        </Box>
    );
}

export default InternalSiteRouter;
